import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useField } from 'formik';
import { FiCheckCircle, FiMaximize, FiZap } from 'react-icons/fi';
import PaginatedZoneSelect from '@/lib/shared-coms/PaginatedZoneSelect/PaginatedZoneSelect';
import ZoneScanButton from '@/lib/shared-coms/ZoneScanner/ZoneScanButton';
import InlineZoneScanner from '@/lib/shared-coms/ZoneScanner/InlineZoneScanner';
import type { ScannedZone } from '@/lib/shared-coms/ZoneScanner/ZoneScanOverlay';
import { useScanner } from '@/root/App/com/ScannerProvider/ScannerProvider';
import { SCAN_MODE } from '@/lib/scanning/scanMode';
import '@/lib/shared-coms/ZoneScanner/ZoneScanner.scss';

export type ZoneSelection = { value: string; display: string; zoneType?: string };

const zoneDisplay = (zone: ScannedZone) =>
  zone.zoneType === 'quarantine' ? `${zone.name} (Quarantine)` : zone.name;

interface ZoneSelectWithScanProps {
  companyId: string;
  locationId?: string;
  excludeZoneIds?: string[];
  zoneTypes?: string[];
  label?: string;
  /** What the scan is for, e.g. "Scan the stocking zone". Falls back to "Scan <label>". */
  scanTitle?: string;
  /** One line telling the operator which zone this step wants (full-screen overlay only). */
  scanHint?: string;
  /** Shown when the scanned zone is in `excludeZoneIds`, e.g. "That's the source zone." */
  excludeMessage?: string;
  /** False for a caller that wants the plain select and no scanning at all. */
  scannable?: boolean;
  /**
   * The page's own, already-live scanner reads this field (the pick sheet's camera, during a
   * Quarantine → Missing). The zone section then says so instead of starting a second scanner —
   * the device runs one at a time — and tells the page through `onScanningChange` whether a scan
   * should land here right now.
   */
  liveScanPrompt?: string;
  onScanningChange?: (scanning: boolean) => void;
  /**
   * Name to show for a value the field did not choose itself — a saved line being edited. Without
   * it such a value is shown in the zone list, which looks its name up.
   */
  valueDisplay?: string;
  form?: boolean;
  name?: string;
  value?: string;
  onChange?: (selection: ZoneSelection) => void;
  onSelect?: (selection: ZoneSelection) => void;
}

/**
 * Tracks zones chosen by scanning so the select can render their names. A scanned zone is
 * frequently absent from the select's first page of results, and without this the field would
 * go blank right after a successful scan.
 */
const useScannedZones = () => {
  const [knownZones, setKnownZones] = useState<ScannedZone[]>([]);
  const remember = useCallback((zone: ScannedZone) => {
    setKnownZones((zones) =>
      zones.some(({ id }) => id === zone.id) ? zones : [...zones, zone]
    );
  }, []);
  return { knownZones, remember };
};

/**
 * The zone field inside the warehouse app's forms (stocking, quarantine, receiving, the pick
 * sheet's quarantine panel).
 *
 * On every device the zone section IS the scanner: a small live camera view (or the "pull
 * the trigger" prompt in laser mode) is already running when the form appears, alongside the
 * form's other fields — never a full-screen scanner that would hide them. A scan fills the zone
 * in; the section then shows the chosen zone, and changing it is one tap. "Choose … from the list"
 * switches to the type-ahead list, which has a way back to the scanner; the X stops scanning and
 * leaves the zone as it was ("tap to scan" until the operator asks again).
 *
 * Where the camera is blocked (and not in laser mode) it is what it always was: the type-ahead
 * select with a scan button beside it.
 *
 * Mirrors `PaginatedZoneSelect`'s two modes — pass `name` for a Formik field, or
 * `form={false}` with `value`/`onChange` for a controlled one.
 */
const ZoneSelectWithScan = ({
  form = true,
  name,
  onSelect,
  ...props
}: ZoneSelectWithScanProps) =>
  form && name ? (
    <FormikZoneField name={name} onSelect={onSelect} {...props} />
  ) : (
    <ControlledZoneField {...props} />
  );

type FieldProps = Omit<ZoneSelectWithScanProps, 'form' | 'name' | 'onSelect'>;

/** Splits the Formik field off so `useField` is never called conditionally. */
const FormikZoneField = ({
  name,
  onSelect,
  ...props
}: FieldProps & { name: string; onSelect?: (selection: ZoneSelection) => void }) => {
  const [field, meta, helpers] = useField(name);
  return (
    <ZoneField
      {...props}
      value={field.value || ''}
      error={meta.touched && meta.error ? String(meta.error) : undefined}
      // Emit through the same callback the dropdown uses, so pages that derive state from a
      // selection (the stocking screens' quarantine notice) behave identically for a scan.
      commit={(selection) => {
        helpers.setValue(selection.value);
        onSelect?.(selection);
      }}
      renderSelect={(knownZones, onListSelect) => (
        <PaginatedZoneSelect
          name={name}
          label={props.label}
          companyId={props.companyId}
          locationId={props.locationId}
          zoneTypes={props.zoneTypes}
          excludeZoneIds={props.excludeZoneIds}
          onSelect={(selection) => {
            onListSelect(selection);
            onSelect?.(selection);
          }}
          knownZones={knownZones}
        />
      )}
    />
  );
};

const ControlledZoneField = ({ value = '', onChange, ...props }: FieldProps) => (
  <ZoneField
    {...props}
    value={value}
    commit={(selection) => onChange?.(selection)}
    renderSelect={(knownZones, onListSelect) => (
      <PaginatedZoneSelect
        form={false}
        label={props.label}
        companyId={props.companyId}
        locationId={props.locationId}
        zoneTypes={props.zoneTypes}
        excludeZoneIds={props.excludeZoneIds}
        value={value}
        onChange={(selection) => {
          onListSelect(selection);
          onChange?.(selection);
        }}
        knownZones={knownZones}
      />
    )}
  />
);

/**
 * What the zone section is showing on a scanning device: the live scanner, the list, the chosen
 * zone, or — after the X — a "tap to scan" card.
 */
type Entry = 'scan' | 'list' | 'chosen' | 'idle';

const ZoneField = ({
  companyId,
  locationId,
  excludeZoneIds,
  zoneTypes,
  label = 'Zone',
  scanTitle,
  scanHint,
  excludeMessage,
  scannable = true,
  liveScanPrompt,
  onScanningChange,
  valueDisplay,
  value,
  error,
  commit,
  renderSelect
}: Omit<FieldProps, 'value' | 'onChange'> & {
  value: string;
  error?: string;
  commit: (selection: ZoneSelection) => void;
  renderSelect: (
    knownZones: ScannedZone[],
    onListSelect: (selection: ZoneSelection) => void
  ) => React.ReactElement;
}) => {
  const { scanFirst, scanMode } = useScanner();
  const { knownZones, remember } = useScannedZones();
  // Names of zones this field chose, by id — a scanned zone, or one picked from the list.
  const [names, setNames] = useState<Record<string, string>>({});
  const chosenName = (value && (names[value] || valueDisplay)) || '';
  const [entry, setEntry] = useState<Entry>(() =>
    value ? (chosenName ? 'chosen' : 'list') : 'scan'
  );

  // A zone landing in the field from anywhere — this scanner, the list, or the page's own scanner
  // — settles the section on it. A value cleared from outside (Back, a form reset) does not start
  // the scanner again by itself: that would be the scanner reopening without being asked.
  const lastValueRef = useRef(value);
  useEffect(() => {
    if (value === lastValueRef.current) return;
    lastValueRef.current = value;
    setEntry(value ? 'chosen' : 'idle');
  }, [value]);

  // The list-first fallback keeps reading every label, as it always did.
  const scanning = !scanFirst || entry === 'scan';
  const onScanningChangeRef = useRef(onScanningChange);
  onScanningChangeRef.current = onScanningChange;
  useEffect(() => {
    onScanningChangeRef.current?.(scanning);
  }, [scanning]);

  const rememberName = (id: string, display: string) =>
    setNames((current) =>
      current[id] === display ? current : { ...current, [id]: display }
    );

  const onScanned = (zone: ScannedZone) => {
    remember(zone);
    const selection = {
      value: zone.id,
      display: zoneDisplay(zone),
      zoneType: zone.zoneType
    };
    rememberName(zone.id, selection.display);
    commit(selection);
    setEntry('chosen');
  };

  const onListSelect = (selection: ZoneSelection) => {
    rememberName(selection.value, selection.display);
    setEntry('chosen');
  };

  const title = scanTitle || `Scan ${label.toLowerCase()}`;
  const listLabel = `Choose ${label.toLowerCase()} from the list`;

  // Camera blocked, or scanning not wanted: the select with a scan button beside it, as always.
  if (!scanFirst || !scannable) {
    return (
      <div className="zone-select-with-scan">
        <div className="zone-select-with-scan__field">
          {renderSelect(knownZones, onListSelect)}
        </div>
        {scannable && !liveScanPrompt && (
          <ZoneScanButton
            companyId={companyId}
            locationId={locationId}
            zoneTypes={zoneTypes}
            excludeZoneIds={excludeZoneIds}
            excludeMessage={excludeMessage}
            title={title}
            hint={scanHint}
            manualEntryLabel={listLabel}
            onSelect={onScanned}
          />
        )}
      </div>
    );
  }

  const errorLine = !!error && entry !== 'list' && (
    <div className="zone-field-error" role="alert">
      {error}
    </div>
  );

  // A zone set from outside the field whose name it was not told: the list looks the name up.
  if (entry === 'list' || (entry === 'chosen' && value && !chosenName)) {
    return (
      <div className="zone-field">
        {renderSelect(knownZones, onListSelect)}
        <button
          type="button"
          className="zone-field-link"
          onClick={() => setEntry('scan')}
        >
          Back to the scanner
        </button>
      </div>
    );
  }

  if (entry === 'chosen' && value) {
    return (
      <div className="zone-field">
        <button
          type="button"
          className="zone-field-card zone-field-card--chosen"
          onClick={() => setEntry('scan')}
          aria-label={`${label}: ${chosenName}. Tap to change`}
        >
          <FiCheckCircle aria-hidden />
          <span className="zone-field-card__text">
            <span className="zone-field-card__label">{label}</span>
            <span className="zone-field-card__value">{chosenName}</span>
          </span>
          <span className="zone-field-card__action">Change</span>
        </button>
        {errorLine}
      </div>
    );
  }

  if (entry === 'scan' && !liveScanPrompt) {
    return (
      <div className="zone-field">
        <InlineZoneScanner
          title={title}
          companyId={companyId}
          locationId={locationId}
          zoneTypes={zoneTypes}
          excludeZoneIds={excludeZoneIds}
          excludeMessage={excludeMessage}
          manualEntryLabel={listLabel}
          onManualEntry={() => setEntry('list')}
          onClose={() => setEntry(value ? 'chosen' : 'idle')}
          onSelect={onScanned}
        />
        {errorLine}
      </div>
    );
  }

  if (entry === 'scan') {
    // The page's own scanner is reading for this field: say so, and keep the same two ways out.
    return (
      <div className="zone-field">
        <div className="zone-field-card" role="status">
          {scanMode === SCAN_MODE.LASER ? (
            <FiZap aria-hidden />
          ) : (
            <FiMaximize aria-hidden />
          )}
          <span className="zone-field-card__text">
            <span className="zone-field-card__label">{label}</span>
            <span className="zone-field-card__prompt">{liveScanPrompt}</span>
          </span>
          {!!value && (
            <button
              type="button"
              className="zone-field-link"
              onClick={() => setEntry('chosen')}
            >
              Keep {chosenName || 'current zone'}
            </button>
          )}
        </div>
        <button
          type="button"
          className="zone-field-link"
          onClick={() => setEntry('list')}
        >
          {listLabel}
        </button>
        {errorLine}
      </div>
    );
  }

  // After the X: nothing scans until the operator asks.
  return (
    <div className="zone-field">
      <button type="button" className="zone-field-card" onClick={() => setEntry('scan')}>
        <FiMaximize aria-hidden />
        <span className="zone-field-card__text">
          <span className="zone-field-card__label">{label}</span>
          <span className="zone-field-card__prompt">Tap to scan the zone label</span>
        </span>
      </button>
      <button type="button" className="zone-field-link" onClick={() => setEntry('list')}>
        {listLabel}
      </button>
      {errorLine}
    </div>
  );
};

export default ZoneSelectWithScan;
